import { forwardRef, useEffect, useRef, useState } from "react";
import { type InputBaseComponentProps } from "@mui/material";
import { IMask, IMaskInput } from "react-imask";
import { formatDuration } from "@WahooFitness/unit-convertr-ts";

const parseDuration = (timeString: string): number | null => {
  const parts = timeString.split(":").map(Number);
  if (parts.some(isNaN)) return null;
  if (parts.length === 1) {
    return parts[0];
  }
  if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  }
  return parts[0] * 3600 + parts[1] * 60 + parts[2];
};

interface CustomProps extends Omit<InputBaseComponentProps, "onChange" | "value"> {
  onChange?: (event: { target: { name: string; value: number | null } }) => void;
  name: string;
  value: number | null;
}

const blocks = {
  SingleDigit9: {
    mask: IMask.MaskedRange,
    from: 0,
    to: 9,
    maxLength: 1,
  },
  DoubleDigit59: {
    mask: IMask.MaskedRange,
    from: 0,
    to: 59,
    maxLength: 2,
  },
  DoubleDigit99: {
    mask: IMask.MaskedRange,
    from: 0,
    to: 99,
    maxLength: 2,
  },
};

const DurationInput = forwardRef<HTMLInputElement, CustomProps>(
  function TextMaskCustom(props, ref) {
    const { onChange, value, ...other } = props;
    const [displayValue, setDisplayValue] = useState(() =>
      value ? formatDuration(value, "full") : ""
    );
    const isInternalChange = useRef(false);

    useEffect(() => {
      if (isInternalChange.current) {
        isInternalChange.current = false;
        return;
      }
      setDisplayValue(value ? formatDuration(value, "full") : "");
    }, [value]);

    return (
      <IMaskInput
        {...other}
        value={displayValue}
        mask={[
          {
            mask: "SingleDigit9",
            blocks: { ...blocks },
          },
          {
            mask: "DoubleDigit59",
            blocks: { ...blocks },
          },
          {
            mask: "SingleDigit9:DoubleDigit59",
            blocks: { ...blocks },
          },
          {
            mask: "DoubleDigit99:DoubleDigit59",
            blocks: { ...blocks },
          },
          {
            mask: "SingleDigit9:DoubleDigit59:DoubleDigit59",
            blocks: { ...blocks },
          },
          {
            mask: "DoubleDigit99:DoubleDigit59:DoubleDigit59",
            blocks: { ...blocks },
          },
        ]}
        inputRef={ref}
        onAccept={(masked: any) => {
          isInternalChange.current = true;
          setDisplayValue(masked);
          onChange?.({
            target: { name: props.name, value: masked ? parseDuration(masked) : null },
          });
        }}
        overwrite
      />
    );
  }
);

export default DurationInput;
